import "./Main.scss";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItemButton from "@mui/material/ListItemButton";
import Logo from "./../../assets/MainLogo.svg";
import NavbarUserMenu from "../../components/NavbarUserMenu/NavbarUserMenu";
import { styled, useTheme } from "@mui/material/styles";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { SidebarList } from "../../SourceData";
import { useMediaQuery } from "@mui/material";
import { usePermissions } from "../../hooks/usePermissions"; // Import the permissions hook

let drawerWidth = 280;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginRight: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",

  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Main = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  drawerWidth = isMobile ? 210 : 280;
  const theme = useTheme();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [activeButton, setActiveButton] = useState(null);

  // Use the permissions hook
  const { hasPermission, isAdmin } = usePermissions();

  useEffect(() => {
    setActiveButton(location.pathname);
  }, [location.pathname]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  // Function to check if a sidebar item should be active
  const isItemActive = (itemPath) => {
    // For root path ("/"), only match exactly
    if (itemPath === "/") {
      return location.pathname === "/";
    }

    // For other paths, check if the current path starts with the item path
    return location.pathname.startsWith(itemPath);
  };

  // Filter sidebar items based on permissions
  const getVisibleSidebarItems = () => {
    return SidebarList.filter((item) => {
      // Always show dashboard (home) for authenticated users
      if (item.path === "/") {
        return true;
      }

      // Admin users can see everything
      if (isAdmin()) {
        return true;
      }

      // Check if user has permission for this specific item
      // Using the id from your SidebarList as the permission identifier
      return hasPermission(item.id);
    });
  };

  const visibleSidebarItems = getVisibleSidebarItems();

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        open={open}
        sx={{
          background:
            "linear-gradient(89.51deg, #019aed -0.41%, #02e4d8 97.31%)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              margin: 0,
              padding: 0,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <div className="user-menu">
            <NavbarUserMenu />
          </div>
        </Toolbar>
      </AppBar>

      <Drawer variant="permanent" open={open} anchor="right">
        <DrawerHeader
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: drawerWidth,
            mt: isMobile ? "6px" : "10px",
          }}
        >
          <img
            src={Logo}
            alt=""
            width={90}
            height={90}
            style={{ margin: "0px auto", objectFit: "cover" }}
          />
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>

        <List>
          {visibleSidebarItems.map((item, index) => (
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={() => {
                setActiveButton(item.path);
              }}
              className={
                isItemActive(item.path) ? "list-item active" : "list-item"
              }
              key={item.id} // Use item.id instead of index for better React keys
              sx={{ position: "relative" }}
            >
              {item.icon}
              {open && item.label}
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          padding: "30px 0px 30px 0px",
          backgroundColor: "#F7F9FB",
          overflow: "auto",
        }}
      >
        <DrawerHeader
          sx={{
            paddingTop: "60px",
            paddingRight: "30px",
            borderBottom: "1px solid  #E1E1E1",
          }}
        >
          <span style={{ width: "100%", fontSize: "14px", color: "#7A808A" }}>
            لوحة التحكم
          </span>
        </DrawerHeader>
        <div className="" style={{ minHeight: "100vh" }}>
          <Outlet />
        </div>
      </Box>
    </Box>
  );
};

export default Main;
